/* eslint-disable @angular-eslint/prefer-standalone */
import { Component, OnInit, inject } from '@angular/core';
import {
	useDefaultDateLocale,
	NotificationService,
	PageLoaderService,
	BreadcrumbService,
	AppBaseComponent,
	LanguageService,
	PageService,
	AppLanguage,
	onLine$,
	useDefaultTimezone,
	TelemetryService,
	DeviceDetectorService,
} from '@aston/foundation';
import { ActivatedRoute, Router, NavigationStart, NavigationEnd } from '@angular/router';
import { delay, filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { AIChatboxSuggestionsService, AIChatPanelService, avaAnchor } from '@aston/ai-chat';
import { frLocale, IPageAnchor, PreloaderService } from '@aston/foundation';
import { NgbModal, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { defineLocale, enGbLocale } from 'ngx-bootstrap/chronos';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, timer } from 'rxjs';
import { Store } from '@ngrx/store';

import { InactivityChannelService, WebSocketService } from './shared-module/services';
import { injectFeaturesStore, observeFeature } from './factor-features-module/stores/factor-features';
import { AuthenticationService } from './authentication-module/services';
import * as AppStoreSelectors from './root-store/app-store/selectors';
import { ReferentialStore } from './shared-module/stores/referential';
import * as AppStoreActions from './root-store/app-store/actions';
import * as RoutesDefinitions from './routes-definitions';
import { AppConstants } from './app.constants';


@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	providers: [
		TranslatePipe
	],
	standalone: false,
})
export class AppComponent extends AppBaseComponent implements OnInit {
	protected aiPanels = inject(AIChatPanelService);
	private telemetryService = inject(TelemetryService);
	translatePipe = inject(TranslatePipe);
	protected ws = inject(WebSocketService);
	private bsLocaleService = inject(BsLocaleService);
	modal = inject(NgbModal);
	private device = inject(DeviceDetectorService);
	private ai = inject(AIChatboxSuggestionsService);
	private inactivityService = inject(InactivityChannelService);
	private breadcrumbService = inject(BreadcrumbService);
	private ngbTooltipConfigService = inject(NgbTooltipConfig);
	private store = inject(Store);
	private preloader = inject(PreloaderService);

	ffs = injectFeaturesStore();

	pageAnchors: IPageAnchor[] = [];

	appIsReady$ = this.store.select(AppStoreSelectors.selectIsReady);
	currentUser$ = this.store.select(AppStoreSelectors.selectCurrentUser);
	isAIChatOpen$ = this.aiPanels.openPanels$.pipe(map(o => o.side));
	bootstrapError$ = this.store.select(AppStoreSelectors.selectBootstrapError);

	location = window.location;
	onLine$ = onLine$;

	constructor() {
		const authenticationService = inject(AuthenticationService);
		const translateService = inject(TranslateService);
		const languageService = inject(LanguageService);
		const pageService = inject(PageService);
		const notificationService = inject(NotificationService);
		const pageLoaderService = inject(PageLoaderService);
		const referential = inject(ReferentialStore);
		const route = inject(ActivatedRoute);
		const router = inject(Router);

		super(translateService, languageService, pageService, notificationService, pageLoaderService, route, router);

		defineLocale(AppLanguage.FR, frLocale);
		defineLocale(AppLanguage.EN, enGbLocale);

		authenticationService.completeLoginCallback().then(() => {
			this.ws.connect();

			this.preloader.closePreloaderWhenPreloadersFinished()
				.then(evt => this.telemetryService.event('[App] Preloader closed', evt, { duration: evt.elapsed  }))
		})

		languageService.language$.subscribe(current => {
			this.bsLocaleService.use(current);
			useDefaultDateLocale(current)
		});

		toObservable(referential.apiTimeZone)
			.pipe(
				filter(Boolean),
				takeUntil(this.destroySubscriptions$)
			)
			.subscribe(tz => useDefaultTimezone(tz));

		combineLatest([
			this.currentUser$.pipe(filter(Boolean), delay(1000)),
			pageService.pageAnchors$,
			observeFeature('isAvaInsightEnabled'),
		]).subscribe(([_, anchors, isAvaInsightEnabled]) => {
			this.pageAnchors = []
				.concat(!this.device.isMobile() ? { ...avaAnchor, onClick: () => this.aiPanels.toggleSideChat() } : [])
				.concat(anchors)
		});

		router.events.pipe(
			filter(event => event instanceof NavigationStart),
			filter(_ => this.modal.hasOpenModals())
		).subscribe(_ => {
			if (window?.alert) alert(this.translatePipe.transform('App.NavigationMessage'));
			this.modal.dismissAll();
		});
		
		router.events.pipe(
			filter(event => event instanceof NavigationEnd),
		).subscribe(_ => {
			this.ai.resetSuggestionsKeys()
			pageService.resetAnchors();

			let url = window.location.pathname;

			if (!url.includes(RoutesDefinitions.AuthenticationRoutePath)) {
				const tenantId = authenticationService.getTenantId();

				// the tenant id is not a part of the route so we remove it from the stored one
				url = url.replace('/' + tenantId + '', '');
				this.store.dispatch(AppStoreActions.UpdateUserSessionRequest({source: `navigating ${url}`, userSession: {lastRoute: url}}));
			}
		});

		translateService.get('Filters.DateExpression')
							 .pipe(take(1))
							 .subscribe(keys => {
								 AppConstants.updateDateExpressionWithTranslation(keys);
							 });

		// Bug 26022: [ITS] [MO-HISTORIQUE DES IMPORTS] : Persistance des tooltips suite à fermeture de la popin de visualisation d'un rapport
		// To prevent tooltip (on buttons) from re-opening after closing a modal that got open when clicking the button
		this.ngbTooltipConfigService.triggers = 'hover';
	}


	ngOnInit() {
		super.ngOnInit();

		this.currentUser$.pipe(
			filter(Boolean),
			switchMap(user => this.translateService.stream('Home.Page.Hello', { name: user.fullName.firstName })),
			tap(message => this.preloader.withPreClosingMessage(message)),
			take(1))
			.subscribe()

		//Improvement 24455: [ACC][Fil d'ariane] Homogéneiser les breadcrumbs de l'application
		this.breadcrumbService.setIgnoredDepth(1);

		timer(0, AppConstants.SECOND)
			.pipe(
				withLatestFrom(this.inactivityService.isInactive$),
				filter(([_, isInactive]) => !isInactive)
			)
			.subscribe(_ => {
				this.store.dispatch(AppStoreActions.UpdateInactivityTimer());
			});
	}
}
